import { useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  loading?: boolean;
  outcome?: 'ok' | 'ko' | null;
  children?: ReactNode;
  full?: boolean;
}

const H: Record<Size, number> = { sm: 32, md: 40, lg: 46 };
const PAD: Record<Size, number> = { sm: 14, md: 18, lg: 22 };
const FS: Record<Size, number> = { sm: 13, md: 14, lg: 15 };

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading,
  outcome,
  children,
  full,
  style,
  disabled,
  ...rest
}: ButtonProps) {
  const [hover, setHover] = useState(false);
  const [down, setDown] = useState(false);
  const isIcon = variant === 'icon';

  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: isIcon ? 36 : H[size],
    width: isIcon ? 36 : full ? '100%' : undefined,
    padding: isIcon ? 0 : `0 ${PAD[size]}px`,
    // la veste pop: bottoni a caramella, tondi, con il bordo in rilievo che si schiaccia
    borderRadius: isIcon ? 'var(--r-md)' : 'var(--r-full)',
    font: `800 ${FS[size]}px/20px var(--font-ui)`,
    whiteSpace: 'nowrap',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background var(--dur-1), border-color var(--dur-1), filter var(--dur-1)',
    transform: down && !disabled ? 'translateY(2px)' : undefined,
    opacity: disabled ? (isIcon ? 0.4 : 0.45) : 1,
    border: '1px solid transparent',
  };

  const skin: Record<Variant, CSSProperties> = {
    primary: {
      background: 'var(--accent)',
      color: 'var(--accent-ink)',
      border: 'none',
      boxShadow: down && !disabled ? '0 2px 0 var(--accent-edge)' : '0 4px 0 var(--accent-edge)',
      filter: hover && !disabled ? 'brightness(1.08)' : down ? 'brightness(0.94)' : undefined,
    },
    secondary: {
      background: hover && !disabled ? 'var(--bg-2)' : 'var(--panel)',
      color: 'var(--text)',
      border: '2px solid',
      borderColor: hover && !disabled ? 'var(--border-strong)' : 'var(--border)',
      boxShadow: down && !disabled ? '0 1px 0 var(--edge)' : '0 3px 0 var(--edge)',
    },
    ghost: {
      background: hover && !disabled ? 'var(--bg-2)' : 'transparent',
      color: hover && !disabled ? 'var(--text)' : 'var(--dim)',
      border: 'none',
    },
    danger: {
      background: hover && !disabled ? 'var(--danger-soft)' : 'transparent',
      color: 'var(--danger)',
      border: '2px solid var(--danger)',
    },
    icon: {
      background: hover && !disabled ? 'var(--bg-2)' : 'transparent',
      color: hover && !disabled ? 'var(--text)' : 'var(--dim)',
      border: 'none',
    },
  };

  const showIcon = outcome === 'ok' ? 'check-circle-2' : outcome === 'ko' ? 'alert-triangle' : icon;
  const iconColor = outcome === 'ok' ? 'var(--ok)' : outcome === 'ko' ? 'var(--danger)' : undefined;

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      style={{ ...base, ...skin[variant], ...style }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setDown(false);
      }}
      onMouseDown={() => setDown(true)}
      onMouseUp={() => setDown(false)}
    >
      {loading ? (
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            flex: 'none',
            border: '2px solid color-mix(in srgb, currentColor 25%, transparent)',
            borderTopColor: 'currentColor',
            animation: 'spin 700ms linear infinite',
          }}
        />
      ) : showIcon ? (
        <Icon name={showIcon} size={isIcon ? 18 : 16} color={iconColor} />
      ) : null}
      {!isIcon && children}
    </button>
  );
}
